
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export const WhatsAppSettings = () => {
  const { toast } = useToast();
  const [delay, setDelay] = useState(3);
  const [signature, setSignature] = useState("Enviado via WA Manager");
  const { currentUser } = useAuth();
  const [companyId, setCompanyId] = useState<string | undefined>(undefined);
  const [isLoadingCompany, setIsLoadingCompany] = useState(true);

  // Ensure we have the company ID even if auth state is temporarily lost
  useEffect(() => {
    const getCompanyId = async () => {
      setIsLoadingCompany(true);
      
      // First try to get from currentUser
      if (currentUser?.companyId) {
        console.log('WhatsAppSettings: Setting company ID from currentUser:', currentUser.companyId);
        setCompanyId(currentUser.companyId);
        setIsLoadingCompany(false);
        return;
      }
      
      // Fallback to localStorage if currentUser is not available
      const savedCompanyId = localStorage.getItem('currentCompanyId');
      if (savedCompanyId) {
        console.log('WhatsAppSettings: Setting company ID from localStorage:', savedCompanyId);
        setCompanyId(savedCompanyId);
        setIsLoadingCompany(false);
      } else {
        console.error('WhatsAppSettings: No company ID available from any source');
        setCompanyId(undefined);
        setIsLoadingCompany(false);
      }
    };
    
    getCompanyId();
  }, [currentUser?.companyId]);

  useEffect(() => {
    if (companyId) {
      fetchWhatsAppSettings();
    }
  }, [companyId]);

  const fetchWhatsAppSettings = async () => {
    if (!companyId) {
      console.error("WhatsAppSettings: Company ID not available");
      return;
    }

    try {
      console.log("WhatsAppSettings: Fetching WhatsApp settings for company:", companyId);
      const { data, error } = await supabase
        .from('whatsapp_settings')
        .select('*')
        .eq('company_id', companyId)
        .maybeSingle();

      if (error) {
        console.error('WhatsAppSettings: Error fetching WhatsApp settings:', error);
        throw error;
      }

      console.log("WhatsAppSettings: WhatsApp settings received:", data);

      if (data) {
        setDelay(data.delay);
        setSignature(data.signature);
      } else {
        console.log("WhatsAppSettings: No WhatsApp settings found for this company, using defaults");
      }
    } catch (error) {
      console.error('WhatsAppSettings: Error fetching WhatsApp settings:', error);
      toast({
        variant: "destructive",
        title: "Erro ao carregar",
        description: "Não foi possível carregar as configurações do WhatsApp.",
      });
    }
  };

  const handleSave = async () => {
    if (!companyId) {
      console.error("WhatsAppSettings: Erro ao salvar: Company ID not available");
      toast({
        variant: "destructive",
        title: "Erro",
        description: "ID da empresa não disponível. Não é possível salvar configurações.",
      });
      return;
    }

    try {
      console.log("WhatsAppSettings: Saving WhatsApp settings for company:", companyId);
      const { error } = await supabase
        .from('whatsapp_settings')
        .upsert({
          company_id: companyId,
          delay,
          signature,
          updated_at: new Date().toISOString()
        });

      if (error) {
        console.error('WhatsAppSettings: Error saving WhatsApp settings:', error);
        throw error;
      }

      console.log("WhatsAppSettings: WhatsApp settings saved successfully");

      toast({
        title: "Sucesso",
        description: "Configurações salvas com sucesso!",
      });
    } catch (error) {
      console.error('WhatsAppSettings: Error saving WhatsApp settings:', error);
      toast({
        variant: "destructive",
        title: "Erro ao salvar",
        description: "Ocorreu um erro ao salvar as configurações.",
      });
    }
  };

  if (isLoadingCompany) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Configurações do WhatsApp</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-primary mr-2"></div>
            <p>Carregando dados da empresa...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!companyId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Configurações do WhatsApp</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-amber-600">Empresa não identificada. Contate o administrador.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Configurações do WhatsApp</CardTitle>
        <p className="text-xs text-muted-foreground">
          Empresa: {companyId.slice(0, 8)}...
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="delay">Delay entre mensagens (segundos)</Label>
          <Input
            id="delay"
            type="number"
            value={delay}
            onChange={(e) => setDelay(Number(e.target.value))}
            placeholder="Ex: 3"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="signature">Assinatura padrão</Label>
          <Input
            id="signature"
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            placeholder="Sua assinatura padrão"
          />
        </div>

        <Button onClick={handleSave} className="w-full">
          Salvar Configurações
        </Button>
      </CardContent>
    </Card>
  );
};
